import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../components/user/BCPAuthProvider";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Tabs, Tab, Container, Grid, Typography } from "@mui/material";
import {
  findGetParameter,
  getData,
  newPath,
} from "../../components/utilities/UtilityFunctions";

import { postNotification } from "../../components/utilities/Notifications";
import ModalLoadingFull from "../../components/modals/ModalLoadingFull";
import TeamUsers from "./components/TeamUsers";
import TeamDetails from "./components/TeamDetails";
import TabPanel, { tabPanelA11yProps } from "../../components/layout/TabPanel";

const tabIndexes = {
  details: 0,
  members: 1,
};

export async function getTeam(teamId, authContext, options) {
  var apiGetData = await getData(`/teams/${teamId}`, {
    queryStringParameters: {
      "expand[]": ["owner"],
    },
  });

  const fetchFile = async (fileId) => {
    if (!fileId) return;

    var returnObj;
    try {
      returnObj = await getData(`/files/${fileId}`, {
        queryStringParameters: {},
      });
    } catch (err) {
      console.error(err);
    }
    return returnObj;
  };

  async function parallel() {
    const taskProfileFile = options?.photo
      ? fetchFile(apiGetData.profileFileId)
      : null;
    const taskBannerFile = options?.photo
      ? fetchFile(apiGetData.bannerFileId)
      : null;

    return {
      profileFile: await taskProfileFile,
      bannerFile: await taskBannerFile,
    };
  }

  var parallelData = await parallel();

  apiGetData.profileFile = parallelData.profileFile;
  apiGetData.bannerFile = parallelData.bannerFile;

  return apiGetData;
}

export default function TeamOrganize() {
  let { teamId } = useParams();
  const authContext = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [team, setTeam] = useState(null);

  const activeTabParam = findGetParameter("active_tab");

  const [activeTab, setActiveTab] = useState(
    !teamId
      ? 0
      : activeTabParam
      ? tabIndexes[activeTabParam.toLowerCase()] !== undefined
        ? tabIndexes[activeTabParam.toLowerCase()]
        : 0
      : 0
  );

  const handleTabChange = async (e, newTab) => {
    setLoading(true);
    try {
      await hasAccess(team, authContext, newTab, true);
    } catch (err) {
      setLoading(false);
      return;
    }

    var path;
    Object.keys(tabIndexes).forEach((tabIndex) => {
      if (tabIndexes[tabIndex] === newTab) {
        path = newPath({
          active_tab: tabIndex,
        });

        return true;
      }
    });
    setActiveTab(newTab);
    navigate(path, { replace: false });
  };

  const isOwner = (_team) => {
    if (!_team?.id) {
      return true;
    }

    if (!authContext?.authState?.identity?.userId) {
      return false;
    }

    if (_team.ownerId === authContext?.authState?.identity?.userId) {
      return true;
    }

    return false;
  };

  const isMember = (_team) => {
    if (!_team?.id) {
      return true;
    }

    if (!authContext?.authState?.identity?.userId) {
      return false;
    }

    if (_team.ownerId === authContext?.authState?.identity?.userId) {
      return true;
    }

    // Temporary access
    if (
      _team.members?.[authContext?.authState?.identity?.userId] ||
      _team.users?.filter(
        (user) => user.objectId === authContext?.authState?.identity?.userId
      )?.length
    ) {
      return true;
    }

    return false;
  };

  useEffect(() => {
    const check = async () => {
      setLoading(true);
      try {
        if (teamId) {
          var apiTeam = await getTeam(teamId, authContext, {
            photo: activeTab === 0,
          });

          apiTeam.isOwner = isOwner(apiTeam);
          apiTeam.isMember = isMember(apiTeam);

          try {
            await hasAccess(apiTeam, authContext, activeTab, true);
          } catch (err) {
            navigate("/account?active_tabs=teams");
            return;
          }

          setTeam(apiTeam);
        } else {
          setTeam({});
        }
      } catch (err) {
        postNotification("error", err);
      }
      setLoading(false);
    };
    check();
    // eslint-disable-next-line
  }, [activeTab]);

  return (
    <>
      {Boolean(loading) && <ModalLoadingFull />}
      {team && (
        <Box sx={{ width: "100%" }}>
          <Box
            sx={{
              maxWidth: "100%",
              
              backgroundColor:
                 "banner.main",
              color:
                "banner.contrastText",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Container maxWidth="lg">
              <Grid container alignItems="center">
                <Grid item xs={12} pr={[2, 0]} pl={[2, 0]}>
                  <Typography variant="h3" gutterBottom>
                    Team: {team.name || "New Team"}
                  </Typography>
                </Grid>
              </Grid>

              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                variant="scrollable"
                scrollButtons="auto"
                aria-label="team tabs"
              >
                <Tab label="Details" {...tabPanelA11yProps(0, "details")} />
                {team?.id && (
                  <Tab label="Users" {...tabPanelA11yProps(1, "users")} />
                )}
              </Tabs>
            </Container>
          </Box>
          <Container
            maxWidth="lg"
            sx={{ pt: 2, pb: 2, backgroundColor: "background.secondary" }}
          >
            <Grid
              container
              sx={{
                gap: 2,
                p: 2,
              }}
            >
              <Grid item xs={12}>
                <TabPanel value={activeTab} index={0}>
                  <TeamDetails
                    team={team}
                    setTeam={setTeam}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    handleTabChange={handleTabChange}
                    authContext={authContext}
                  />
                </TabPanel>
                <TabPanel value={activeTab} index={1}>
                  <TeamUsers
                    team={team}
                    setTeam={setTeam}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    handleTabChange={handleTabChange}
                    authContext={authContext}
                  />
                </TabPanel>
              </Grid>
            </Grid>
          </Container>
        </Box>
      )}
    </>
  );
}

export function TeamTabs(props) {
  const { team, activeTab, handleTabChange } = props;

  return (
    <Box
      sx={{
        maxWidth: "100%",
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="team tabs"
      >
        <Tab label="Details" {...tabPanelA11yProps(0, "details")} />
        {team?.id && <Tab label="Users" {...tabPanelA11yProps(1, "users")} />}
      </Tabs>
    </Box>
  );
}

export async function hasAccess() {
  return true;
}
